import { useRouter, useTransitionHook } from "@uirouter/react";
import React from "react";
import { isLeavingReview, type CloseBlock } from "./reviewRules";

// The review closes by URL (it is open while `reviewDocumentTypeId` is in the URL), so besides
// its close button and Escape, browser Back/Forward and in-app navigation also close it. This
// guard catches those too:
// - a UI-Router transition that would leave the review is aborted while something blocks closing
//   (UI-Router then restores the URL) and `onBlocked` decides: wait for a save, or confirm;
//   `proceed` re-runs the same navigation once the reviewer agreed;
// - with edits of a submitted document not submitted again (`mustAskToSubmit`), it is aborted too
//   and `onAskToSubmit` asks whether to submit them first; `proceed` re-runs the navigation once
//   the reviewer chose;
// - a full page unload (reload, closing the tab) gets the browser's "leave site?" prompt.

const REVIEW_PARAM = "reviewDocumentTypeId";

interface Params {
  /** Latest reason closing must wait or ask (null = close freely). Read at navigation time. */
  getCloseBlock: () => CloseBlock | null;
  /** Latest: whether leaving must ask to submit the edits first. Read at navigation time. */
  mustAskToSubmit: () => boolean;
  /** Ask before a full page unload (unsent work, or edits not submitted). */
  warnOnUnload: boolean;
  onAskToSubmit: (proceed: () => void) => void;
  onBlocked: (block: CloseBlock, proceed: () => void) => void;
}

export function useReviewCloseGuard(params: Params) {
  const { stateService } = useRouter();
  const allowLeaveRef = React.useRef(false);

  useTransitionHook("onBefore", {}, (transition) => {
    const leaving = isLeavingReview({
      fromState: transition.from().name ?? "",
      toState: transition.to().name ?? "",
      fromReviewParam: transition.params("from")[REVIEW_PARAM],
      toReviewParam: transition.params("to")[REVIEW_PARAM],
    });
    if (!leaving || allowLeaveRef.current) {
      return true;
    }
    const block = params.getCloseBlock();
    if (block === null && !params.mustAskToSubmit()) {
      return true;
    }
    const target = transition.to();
    const targetParams = transition.params("to");
    const proceed = () => {
      allowLeaveRef.current = true;
      void stateService.go(target, targetParams);
    };
    if (block === null) {
      params.onAskToSubmit(proceed);
    } else {
      params.onBlocked(block, proceed);
    }
    return false;
  });

  const { warnOnUnload } = params;
  React.useEffect(() => {
    if (!warnOnUnload) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [warnOnUnload]);

  return {
    /** Lets the next navigation out of the review through (after the reviewer confirmed). */
    allowLeave: () => {
      allowLeaveRef.current = true;
    },
  };
}
